import { useEffect, useMemo, useRef, useState } from 'react';
import { MapPin, Search, Globe, Phone, Award, Loader2, ExternalLink, Crosshair } from 'lucide-react';
import { useGoogleMaps } from '@/hooks/useGoogleMaps';
import {
  useNearbyStoresSearch,
  useCommunityStores,
  useStoreBlocklist,
  type NearbyStore,
} from '@/hooks/useNearbyStores';
import { toast } from 'sonner';

type DisplayStore = {
  id: string;
  name: string;
  address: string;
  lat: number;
  lng: number;
  website: string | null;
  phone: string | null;
  distance: number | null;
  verified: boolean;
  hosts_tournaments: boolean;
  source: 'community' | 'places';
};

const DISTANCE_OPTIONS = [5, 10, 25, 50, 100] as const;

function haversineMiles(a: { lat: number; lng: number }, b: { lat: number; lng: number }) {
  const R = 3958.8;
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const x = Math.sin(dLat/2) ** 2 +
    Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) *
    Math.sin(dLng/2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(x));
}

export function StoresTab() {
  const [address, setAddress] = useState('');
  const [radius, setRadius] = useState<number>(25);
  const [center, setCenter] = useState<{ lat: number; lng: number } | null>(null);

  const search = useNearbyStoresSearch();
  const { data: community = [] } = useCommunityStores();
  const { data: blocklist } = useStoreBlocklist();
  const { ready: mapsReady, error: mapsError } = useGoogleMaps();

  const mapDivRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<any>(null);
  const markersRef = useRef<any[]>([]);

  const submit = async (e?: React.FormEvent) => {
    e?.preventDefault();
    if (!address.trim()) {
      toast.error('Enter a ZIP code or address');
      return;
    }
    try {
      const res = await search.mutateAsync({ address: address.trim(), radiusMiles: radius });
      if (res.center) setCenter(res.center);
      else toast.error('Could not locate that address');
    } catch (err: any) {
      toast.error(err?.message ?? 'Search failed');
    }
  };

  const useMyLocation = () => {
    if (!navigator.geolocation) {
      toast.error('Location not available');
      return;
    }
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const { latitude, longitude } = pos.coords;
        setAddress(`${latitude.toFixed(4)}, ${longitude.toFixed(4)}`);
        try {
          const res = await search.mutateAsync({ lat: latitude, lng: longitude, radiusMiles: radius });
          if (res.center) setCenter(res.center);
        } catch (err: any) { toast.error(err?.message ?? 'Search failed'); }
      },
      () => toast.error('Location permission denied'),
    );
  };

  // Merge community stores (always shown when near center) + Places results,
  // de-duped by Google place id, with blocklist applied.
  const displayStores: DisplayStore[] = useMemo(() => {
    if (!center) return [];
    const placeIdsSeen = new Set<string>();
    const out: DisplayStore[] = [];

    const communityNear = community
      .map(s => ({ s, distance: haversineMiles(center, { lat: s.latitude, lng: s.longitude }) }))
      .filter(({ distance }) => distance <= radius)
      .map(({ s, distance }) => {
        if (s.google_place_id) placeIdsSeen.add(s.google_place_id);
        return {
          id: `c-${s.id}`,
          name: s.name,
          address: s.address,
          lat: s.latitude,
          lng: s.longitude,
          website: s.website,
          phone: s.phone,
          distance: Math.round(distance * 10) / 10,
          verified: s.verified,
          hosts_tournaments: s.hosts_tournaments,
          source: 'community' as const,
        };
      });
    out.push(...communityNear);

    const places = (search.data?.stores ?? []).filter(p => {
      if (placeIdsSeen.has(p.placeId)) return false;
      if (blocklist?.has(p.placeId)) return false;
      return true;
    });
    for (const p of places) {
      out.push({
        id: `p-${p.placeId}`,
        name: p.name,
        address: p.address,
        lat: p.lat,
        lng: p.lng,
        website: p.website,
        phone: p.phone,
        distance: p.distance,
        verified: false,
        hosts_tournaments: false,
        source: 'places',
      });
    }

    return out.sort((a, b) => (a.distance ?? 0) - (b.distance ?? 0));
  }, [center, radius, community, search.data, blocklist]);

  // Initialize map once
  useEffect(() => {
    if (!mapsReady || !mapDivRef.current || mapRef.current || !window.google?.maps) return;
    mapRef.current = new window.google.maps.Map(mapDivRef.current, {
      center: center ?? { lat: 39.5, lng: -98.35 },
      zoom: center ? 11 : 4,
      disableDefaultUI: false,
      mapTypeControl: false,
      streetViewControl: false,
      fullscreenControl: false,
      styles: [
        { elementType: 'geometry', stylers: [{ color: '#1a1c2e' }] },
        { elementType: 'labels.text.fill', stylers: [{ color: '#c9d0e6' }] },
        { elementType: 'labels.text.stroke', stylers: [{ color: '#0a0b1a' }] },
        { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#0f1226' }] },
        { featureType: 'road', elementType: 'geometry', stylers: [{ color: '#2a2d44' }] },
        { featureType: 'poi', stylers: [{ visibility: 'off' }] },
      ],
    });
  }, [mapsReady, center]);

  // Re-render markers when stores change
  useEffect(() => {
    if (!mapRef.current || !window.google?.maps) return;
    markersRef.current.forEach(m => m.setMap(null));
    markersRef.current = [];

    const bounds = new window.google.maps.LatLngBounds();

    if (center) {
      mapRef.current.setCenter(center);
      markersRef.current.push(new window.google.maps.Marker({
        position: center,
        map: mapRef.current,
        icon: {
          path: window.google.maps.SymbolPath.CIRCLE,
          scale: 9,
          fillColor: '#d4a843',
          fillOpacity: 0.9,
          strokeColor: '#0a0b1a',
          strokeWeight: 2,
        },
        title: 'You',
      }));
      bounds.extend(center);
    }

    for (const s of displayStores) {
      const marker = new window.google.maps.Marker({
        position: { lat: s.lat, lng: s.lng },
        map: mapRef.current,
        title: s.name,
      });
      const info = new window.google.maps.InfoWindow({
        content: `<div style="color:#0a0b1a;font-family:Inter,sans-serif;max-width:220px">
          <strong>${escapeHtml(s.name)}</strong><br/>
          <span style="font-size:12px">${escapeHtml(s.address)}</span>
          ${s.website ? `<br/><a href="${escapeAttr(s.website)}" target="_blank" rel="noopener" style="color:#0a4ca6;font-size:12px">Website ↗</a>` : ''}
        </div>`,
      });
      marker.addListener('click', () => info.open({ anchor: marker, map: mapRef.current }));
      markersRef.current.push(marker);
      bounds.extend({ lat: s.lat, lng: s.lng });
    }

    if (displayStores.length > 0) {
      mapRef.current.fitBounds(bounds, 40);
    }
  }, [displayStores, center]);

  return (
    <div className="px-4 pb-6">
      <header className="mt-3">
        <h2 className="font-display text-xl font-semibold text-foreground">Find Lorcana Stores</h2>
        <p className="text-xs text-muted-foreground font-body mt-1">
          Local game shops that carry Lorcana and run tournaments. Big-box retailers (Target, Walmart, etc.) are filtered out.
        </p>
      </header>

      <form onSubmit={submit} className="mt-4 space-y-2">
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <input
              type="text"
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              placeholder="ZIP code or address"
              aria-label="Address or ZIP code"
              maxLength={200}
              className="w-full rounded-lg border border-border bg-card py-2.5 pl-9 pr-3 text-sm text-foreground font-body placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary"
            />
          </div>
          <button
            type="button"
            onClick={useMyLocation}
            title="Use my current location"
            className="flex h-10 w-10 items-center justify-center rounded-lg border border-border bg-card text-muted-foreground hover:text-primary hover:border-primary/40 transition-colors"
          >
            <Crosshair className="h-4 w-4" />
          </button>
        </div>
        <div className="flex items-center gap-2">
          <label className="text-[10px] uppercase tracking-wider text-muted-foreground font-body">Within</label>
          <div className="flex flex-wrap gap-1.5">
            {DISTANCE_OPTIONS.map(d => (
              <button
                key={d}
                type="button"
                onClick={() => setRadius(d)}
                className={`rounded-full px-2.5 py-1 text-[10px] font-medium font-body transition-colors ${
                  radius === d ? 'bg-primary text-primary-foreground' : 'bg-secondary text-secondary-foreground'
                }`}
              >
                {d} mi
              </button>
            ))}
          </div>
          <button
            type="submit"
            disabled={search.isPending}
            className="ml-auto flex items-center gap-1.5 rounded-lg bg-primary px-4 py-2 text-xs font-medium text-primary-foreground font-body disabled:opacity-50"
          >
            {search.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Search className="h-3.5 w-3.5" />}
            Search
          </button>
        </div>
      </form>

      {/* Map */}
      <div className="mt-4 rounded-xl border border-border overflow-hidden bg-card">
        {mapsError ? (
          <div className="aspect-[16/10] flex items-center justify-center text-xs text-muted-foreground p-4 text-center">
            Map unavailable: {mapsError}
          </div>
        ) : !mapsReady ? (
          <div className="aspect-[16/10] flex items-center justify-center">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <div ref={mapDivRef} className="aspect-[16/10] w-full md:aspect-[16/8]" />
        )}
      </div>

      {/* Results */}
      <div className="mt-5">
        {!center ? (
          <div className="text-center py-10 text-sm text-muted-foreground font-body">
            <MapPin className="h-8 w-8 mx-auto mb-2 opacity-50" />
            Enter your ZIP code to find Lorcana stores near you.
          </div>
        ) : displayStores.length === 0 ? (
          <div className="text-center py-10 text-sm text-muted-foreground font-body">
            {search.isPending ? 'Searching…' : 'No Lorcana-friendly stores found in this area. Try a larger radius.'}
          </div>
        ) : (
          <>
            <h3 className="font-display text-base font-semibold text-foreground mb-2">
              {displayStores.length} store{displayStores.length === 1 ? '' : 's'} near you
            </h3>
            <ul className="space-y-2">
              {displayStores.map(s => <StoreCard key={s.id} store={s} />)}
            </ul>
          </>
        )}
      </div>

      <p className="mt-6 text-[10px] text-muted-foreground/70 font-body text-center">
        Know a great Lorcana shop missing from this list? More submission tools coming soon.
      </p>
    </div>
  );
}

function StoreCard({ store }: { store: DisplayStore }) {
  const directionsUrl = `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(store.address)}`;
  return (
    <li className="rounded-xl border border-border bg-card p-3">
      <div className="flex items-start gap-2">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 flex-wrap">
            <h4 className="font-display text-sm font-semibold text-foreground truncate">{store.name}</h4>
            {store.verified && (
              <span className="inline-flex items-center gap-0.5 rounded-full bg-ink-emerald/15 text-ink-emerald px-1.5 py-0.5 text-[9px] font-bold font-body">
                <Award className="h-2.5 w-2.5" /> Verified
              </span>
            )}
            {store.hosts_tournaments && (
              <span className="rounded-full bg-primary/15 text-primary px-1.5 py-0.5 text-[9px] font-bold font-body">
                Tournaments
              </span>
            )}
          </div>
          <p className="mt-0.5 text-[11px] text-muted-foreground font-body line-clamp-2">{store.address}</p>
          <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-muted-foreground font-body">
            {store.distance != null && <span>{store.distance} mi</span>}
            {store.phone && (
              <a href={`tel:${store.phone}`} className="inline-flex items-center gap-1 hover:text-primary">
                <Phone className="h-3 w-3" /> {store.phone}
              </a>
            )}
            {store.website && (
              <a href={store.website} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 hover:text-primary">
                <Globe className="h-3 w-3" /> Website <ExternalLink className="h-2.5 w-2.5" />
              </a>
            )}
            <a href={directionsUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 hover:text-primary">
              <MapPin className="h-3 w-3" /> Directions
            </a>
          </div>
        </div>
      </div>
    </li>
  );
}

function escapeHtml(s: string) {
  return s.replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]!));
}
function escapeAttr(s: string) { return escapeHtml(s); }
